import React, { Component } from 'react';
import {
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Paper,
    Card,
    CardContent,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CodeIcon from '@mui/icons-material/Code';
import ArchiveIcon from '@mui/icons-material/Archive';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { listFilesBycategoryForGroup, updateFile, deleteFile } from './apiFiles';
import { isAuthenticated } from '../auth';
import SecureLink from './secureLink';


class FileList extends Component {

    constructor(props) {
        super(props);
        this.state = {
            categories: [],
            groupId: this.props.groupId,
            loading: true,
            expanded: 'panel0',
            editDialogOpen: false,
            editingFile: null,
            editTitle: '',
            editDescription: '',
            editFileCategory: '',
            editError: '',
            editSaving: false,
            deleteDialogOpen: false,
            deletingFile: null,
            deleteLoading: false
        };
    }

    canEdit = () => {
        const auth = isAuthenticated();
        if (!auth) return false;
        if (auth.role === 'admin') return true;
        if (this.props.chairId && auth.userId === this.props.chairId) return true;
        if (this.props.cochairId && auth.userId === this.props.cochairId) return true;
        return false;
    };

    handleAccordionChange = (panel) => (event, isExpanded) => {
        this.setState({ expanded: isExpanded ? panel : false });
    };

    listFiles = () => {
        this.setState({ loading: true });
        listFilesBycategoryForGroup(this.state.groupId, this.token).then(data => {
            if (data.error) {
                console.log(data.error);
                this.setState({ loading: false });
            } else {
                this.setState({
                    categories: data,
                    loading: false
                });
            }
        });
    };

    componentDidMount() {
        this.listFiles();
    }

    handleEditClick = (file) => {
        this.setState({
            editDialogOpen: true,
            editingFile: file,
            editTitle: file.title || '',
            editDescription: file.description || '',
            editFileCategory: file.fileCategory || '',
            editError: ''
        });
    };

    handleEditClose = () => {
        this.setState({
            editDialogOpen: false,
            editingFile: null,
            editTitle: '',
            editDescription: '',
            editFileCategory: '',
            editError: '',
            editSaving: false
        });
    };

    handleDeleteClick = (file) => {
        this.setState({ deleteDialogOpen: true, deletingFile: file });
    };

    handleDeleteClose = () => {
        this.setState({ deleteDialogOpen: false, deletingFile: null, deleteLoading: false });
    };

    handleDeleteConfirm = () => {
        const { deletingFile } = this.state;
        const userId = isAuthenticated() && isAuthenticated().userId;

        this.setState({ deleteLoading: true });
        deleteFile(deletingFile._id, userId).then(data => {
            if (data && data.error) {
                this.setState({ deleteLoading: false });
            } else {
                this.handleDeleteClose();
                this.listFiles();
            }
        }).catch(() => {
            this.setState({ deleteLoading: false });
        });
    };

    handleEditSave = () => {
        const { editingFile, editTitle, editDescription, editFileCategory } = this.state;
        const userId = isAuthenticated() && isAuthenticated().userId;

        if (!editTitle.trim()) {
            this.setState({ editError: 'Title is required' });
            return;
        }

        this.setState({ editSaving: true });
        updateFile(editingFile._id, userId, {
            title: editTitle,
            description: editDescription,
            fileCategory: editFileCategory
        }).then(data => {
            if (data && data.error) {
                this.setState({ editError: data.error, editSaving: false });
            } else {
                this.handleEditClose();
                this.listFiles();
            }
        }).catch(() => {
            this.setState({ editError: 'Failed to update file', editSaving: false });
        });
    };

    // Get icon based on file extension or type
    getFileIcon = (fileName) => {
        if (!fileName) return <InsertDriveFileIcon />;

        const ext = fileName.toLowerCase().split('.').pop();
        const iconProps = { sx: { fontSize: 24 } };

        switch (ext) {
            case 'pdf':
                return <PictureAsPdfIcon {...iconProps} sx={{ color: '#d32f2f' }} />;
            case 'doc':
            case 'docx':
                return <DescriptionIcon {...iconProps} sx={{ color: '#2196f3' }} />;
            case 'xls':
            case 'xlsx':
            case 'csv':
                return <TableChartIcon {...iconProps} sx={{ color: '#4caf50' }} />;
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'svg':
                return <ImageIcon {...iconProps} sx={{ color: '#ff9800' }} />;
            case 'mp4':
            case 'avi':
            case 'mov':
            case 'wmv':
                return <VideoLibraryIcon {...iconProps} sx={{ color: '#9c27b0' }} />;
            case 'zip':
            case 'rar':
            case '7z':
                return <ArchiveIcon {...iconProps} sx={{ color: '#795548' }} />;
            case 'html':
            case 'css':
            case 'js':
            case 'json':
            case 'xml':
                return <CodeIcon {...iconProps} sx={{ color: '#607d8b' }} />;
            default:
                return <InsertDriveFileIcon {...iconProps} sx={{ color: '#003E7E' }} />;
        }
    };

    // Get color for category based on index
    getCategoryColor = (index) => {
        const colors = ['#003E7E', '#FF6600', '#4caf50', '#9c27b0', '#f44336', '#2196f3'];
        return colors[index % colors.length];
    };

    renderFiles = (categories) => {
        if (this.state.loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress size={60} sx={{ color: '#003E7E', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#003E7E' }}>Loading files...</Typography>
                    </Box>
                </Box>
            );
        }

        if (!categories || categories.length === 0) {
            return (
                <Card elevation={2} sx={{ p: 5, textAlign: 'center', borderRadius: 3, backgroundColor: 'rgba(0, 62, 126, 0.02)' }}>
                    <FolderIcon sx={{ fontSize: 64, color: '#003E7E', opacity: 0.3, mb: 2 }} />
                    <Typography variant="h6" sx={{ color: '#003E7E', mb: 1 }}>
                        No Files Available
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Files shared with this committee will appear here
                    </Typography>
                </Card>
            );
        }

        return (
            <Box>
                {categories.map((category, i) => {
                    const panelId = `panel${i}`;
                    const categoryColor = this.getCategoryColor(i);
                    const { expanded } = this.state;
                    const isExpanded = expanded === panelId;

                    return (
                        <Accordion
                            key={i}
                            expanded={isExpanded}
                            onChange={this.handleAccordionChange(panelId)}
                            elevation={3}
                            sx={{
                                mb: 2,
                                borderRadius: 2,
                                overflow: 'hidden',
                                '&:before': {
                                    display: 'none'
                                },
                                border: isExpanded ? `2px solid ${categoryColor}` : 'none'
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    backgroundColor: isExpanded ? categoryColor : 'rgba(0, 62, 126, 0.05)',
                                    color: isExpanded ? 'white' : '#003E7E',
                                    '&:hover': {
                                        backgroundColor: isExpanded ? categoryColor : 'rgba(0, 62, 126, 0.1)'
                                    },
                                    transition: 'all 0.3s ease',
                                    minHeight: 64,
                                    '& .MuiAccordionSummary-content': {
                                        my: 2,
                                        alignItems: 'center'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    <FolderIcon sx={{ fontSize: 32 }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {category._id}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={`${category.count} ${category.count === 1 ? 'File' : 'Files'}`}
                                        sx={{
                                            backgroundColor: isExpanded ? 'rgba(255, 255, 255, 0.2)' : categoryColor,
                                            color: 'white',
                                            fontWeight: 600
                                        }}
                                    />
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, backgroundColor: 'white' }}>
                                <List disablePadding>
                                    {category.data.map((file, j) => (
                                        <React.Fragment key={j}>
                                            <ListItem
                                                sx={{
                                                    px: 3,
                                                    py: 2,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 62, 126, 0.05)',
                                                        transform: 'translateX(8px)'
                                                    }
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 40 }}>
                                                    {this.getFileIcon(file.fileName)}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <SecureLink file={file} />
                                                        </Box>
                                                    }
                                                    secondary={file.description || file.uploadedBy?.name}
                                                    primaryTypographyProps={{
                                                        sx: { fontWeight: 500, color: '#003E7E' }
                                                    }}
                                                />
                                                {this.canEdit() && (
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => this.handleEditClick(file)}
                                                            sx={{ color: '#003E7E', opacity: 0.6, '&:hover': { opacity: 1 } }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => this.handleDeleteClick(file)}
                                                            sx={{ color: '#d32f2f', opacity: 0.6, '&:hover': { opacity: 1 } }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </ListItem>
                                            {j < category.data.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>
        );
    };

    render() {
        const { categories, editDialogOpen, editTitle, editDescription, editError, editSaving,
            deleteDialogOpen, deletingFile, deleteLoading } = this.state;
        return (
            <Box>
                {this.renderFiles(categories)}

                <Dialog open={editDialogOpen} onClose={this.handleEditClose} maxWidth="sm" fullWidth>
                    <DialogTitle>Edit File</DialogTitle>
                    <DialogContent>
                        {editError && (
                            <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>
                        )}
                        <TextField
                            fullWidth
                            label="Title"
                            value={editTitle}
                            onChange={(e) => this.setState({ editTitle: e.target.value, editError: '' })}
                            margin="normal"
                            variant="outlined"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={editDescription}
                            onChange={(e) => this.setState({ editDescription: e.target.value })}
                            margin="normal"
                            variant="outlined"
                            multiline
                            rows={3}
                        />
                        <FormControl fullWidth margin="normal" variant="outlined">
                            <InputLabel>File Category</InputLabel>
                            <Select
                                value={this.state.editFileCategory}
                                onChange={(e) => this.setState({ editFileCategory: e.target.value })}
                                label="File Category"
                            >
                                <MenuItem value=""><em>None</em></MenuItem>
                                <MenuItem value="SDSIC Leadership">SDSIC Leadership</MenuItem>
                                <MenuItem value="SDSIC Position Statement">SDSIC Position Statement</MenuItem>
                                <MenuItem value="SDSIC Proposals">SDSIC Proposals</MenuItem>
                                <MenuItem value="SDSIC Newsletter">SDSIC Newsletter</MenuItem>
                                <MenuItem value="SDSIC Event Links">SDSIC Event Links</MenuItem>
                                <MenuItem value="SDSIC Event Presentations">SDSIC Event Presentations</MenuItem>
                                <MenuItem value="SDSIC Event Handouts">SDSIC Event Handouts</MenuItem>
                                <MenuItem value="Lisp File">Lisp Files</MenuItem>
                                <MenuItem value="Script File">Script Files</MenuItem>
                                <MenuItem value="iLogic File">iLogic Files</MenuItem>
                                <MenuItem value="Training">Training</MenuItem>
                                <MenuItem value="Best Practices">Best Practices</MenuItem>
                                <MenuItem value="Presentation">Presentation</MenuItem>
                                <MenuItem value="White Paper">White Paper</MenuItem>
                                <MenuItem value="Meeting Agenda">Meeting Agenda</MenuItem>
                                <MenuItem value="Meeting Invite">Meeting Invite</MenuItem>
                                <MenuItem value="Meeting Minutes">Meeting Minutes</MenuItem>
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={this.handleEditClose} disabled={editSaving}>
                            Cancel
                        </Button>
                        <Button
                            onClick={this.handleEditSave}
                            variant="contained"
                            disabled={editSaving}
                        >
                            {editSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={deleteDialogOpen} onClose={this.handleDeleteClose} maxWidth="xs" fullWidth>
                    <DialogTitle>Delete File</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete <strong>{deletingFile && deletingFile.title}</strong>? This cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={this.handleDeleteClose} disabled={deleteLoading}>
                            Cancel
                        </Button>
                        <Button
                            onClick={this.handleDeleteConfirm}
                            variant="contained"
                            color="error"
                            disabled={deleteLoading}
                        >
                            {deleteLoading ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }
}

export default FileList;